import { useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { buildTyreMove, TYRE_MOVE_TOASTS, type TyreMoveContext, type TyreMoveFormValues, type TyreMoveKind } from '../policies/tyre-move.policy';
import { moveTyre } from '../tyres.api';

/** Records one move against a tyre. The server's refusal ("that position already holds a tyre") stays inline in the modal; a recorded move toasts and reloads what is on screen. */
export function useTyreMove(afterMove: () => Promise<unknown>) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function run(kind: TyreMoveKind, subject: { id: string; serial: string }, values: TyreMoveFormValues, context: TyreMoveContext = {}): Promise<boolean> {
    setError(null);
    setSaving(true);
    try {
      const request = buildTyreMove(kind, subject.id, values, context);
      await moveTyre(request.tyreId, request.action, request.body);
      await afterMove();
      toast.success(TYRE_MOVE_TOASTS[kind](subject.serial));
      return true;
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Could not record this — check the fields and try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  return { error, saving, resetError: () => setError(null), run };
}
